import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Calendar, Upload, Eye, EyeOff, Save, RefreshCw, Image as ImageIcon, Trash2, Plus, X } from 'lucide-react';
import axios from 'axios';
import '../styles/premium-theme.css';
import '../styles/dashboard-premium.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const DailyChallengeCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewImages, setPreviewImages] = useState({});

  // Normalize date string to YYYY-MM-DD for <input type="date">
  const toDateInput = (val) => {
    try {
      if (!val) return new Date().toISOString().split('T')[0];
      // If already yyyy-mm-dd
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
      return d.toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  // Save all 3 cards in one go without losing unsaved images
  const saveAllCards = async () => {
    try {
      setSaving(true);
      for (let i = 0; i < cards.length; i++) {
        const c = cards[i];
        // Skip empty slots with no image and no existing id
        if (!c?._id && !c?.imageUrl) continue;
        // Ensure scheduleTime default
        if (!c?.scheduleTime && !c?.scheduledTime) {
          handleInputChange(i, 'scheduleTime', ['15:00','18:00','21:00'][i] || '15:00');
        }
        // Save sequentially to avoid conflicts
        await handleSaveCard(i);
      }
      // After all saved, refetch once to align with server
      await fetchTodaysCards();
      toast.success('All cards saved');
    } catch (e) {
      // handleSaveCard already toasts errors; keep minimal here
    } finally {
      setSaving(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchTodaysCards = async () => {
    try {
      setLoading(true);
      const todayISO = new Date().toISOString().split('T')[0];
      const response = await axios.get(
        `${API_BASE_URL}/auth/admin/broadcast-cards`,
        {
          headers: getAuthHeaders(),
          params: {
            date: todayISO,
            page: 1,
            limit: 3
          }
        }
      );
      
      if (response.data.success) {
        const allCards = response.data.cards || [];
        // Only today's cards are returned from API; still sort by scheduleTime asc for consistency
        const todaysCards = [...allCards].sort((a,b) => String(a.scheduleTime||'').localeCompare(String(b.scheduleTime||'')));
        // Ensure we always have exactly 3 cards
        const paddedCards = [...todaysCards];
        while (paddedCards.length < 3) {
          paddedCards.push({
            title: `Card ${paddedCards.length + 1}`,
            description: '',
            imageUrl: '',
            isPublished: false,
            scheduledDate: new Date().toISOString().split('T')[0],
            scheduleTime: ['15:00','18:00','21:00'][paddedCards.length] || '15:00',
            targetPlan: 'all'
          });
        }
        setCards(paddedCards);
      } else {
        // Create empty cards if no data
        const emptyCards = [];
        for (let i = 0; i < 3; i++) {
          emptyCards.push({
            title: `Card ${i + 1}`,
            description: '',
            imageUrl: '',
            isPublished: false,
            scheduledDate: new Date().toISOString().split('T')[0],
            scheduleTime: ['15:00','18:00','21:00'][i] || '15:00',
            targetPlan: 'all'
          });
        }
        setCards(emptyCards);
      }
    } catch (error) {
      console.error('Error fetching cards:', error);
      // Create empty cards on error
      const emptyCards = [];
      for (let i = 0; i < 3; i++) {
        emptyCards.push({
          title: `Card ${i + 1}`,
          description: '',
          imageUrl: '',
          isPublished: false,
          scheduledDate: new Date().toISOString().split('T')[0],
          scheduleTime: ['15:00','18:00','21:00'][i] || '15:00',
          targetPlan: 'all'
        });
      }
      setCards(emptyCards);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodaysCards();
  }, []);

  const handleInputChange = (index, field, value) => {
    const updatedCards = [...cards];
    updatedCards[index] = { ...updatedCards[index], [field]: value };
    setCards(updatedCards);
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/admin/upload-broadcast-card-image`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const data = response?.data || {};
      const ok = data.success === true || data.status === 'success';
      const uploadedUrl = data.imageUrl || data?.data?.imageUrl;

      if (ok && uploadedUrl) {
        handleInputChange(index, 'imageUrl', uploadedUrl);
        toast.success('Image uploaded successfully');
      } else {
        const msg = data.message || data.error || 'Failed to upload image';
        toast.error(msg);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to upload image';
      toast.error(msg);
    }
  };

  const handleImagePreview = (index, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImages(prev => ({
          ...prev,
          [index]: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveCard = async (index) => {
    const card = cards[index];
    
    if (!card.title.trim()) {
      toast.error('Please enter a title for the card');
      return;
    }

    try {
      setSaving(true);

      // Backend requires `scheduleTime` (HH:mm). Provide sensible defaults per slot.
      const defaultSlots = ['15:00', '18:00', '21:00'];
      const scheduleTime = card.scheduleTime || card.scheduledTime || defaultSlots[index] || '15:00';

      const cardData = {
        title: card.title,
        description: card.description || 'Daily Challenge Card',
        imageUrl: card.imageUrl,
        isPublished: !!card.isPublished,
        isActive: card.isActive !== false, // default true
        scheduledDate: card.scheduledDate,
        scheduleTime,
        targetPlan: card.targetPlan || 'all'
      };

      let response;
      if (card._id) {
        // Update existing card
        response = await axios.put(
          `${API_BASE_URL}/auth/admin/broadcast-cards/${card._id}`,
          cardData,
          { headers: getAuthHeaders() }
        );
      } else {
        // Create new card
        response = await axios.post(
          `${API_BASE_URL}/auth/admin/broadcast-cards`,
          cardData,
          { headers: getAuthHeaders() }
        );
      }

      const data = response?.data || {};
      const ok = data.success === true || data.status === 'success';
      if (ok) {
        // Update the card with the response data
        const updatedCards = [...cards];
        updatedCards[index] = { ...updatedCards[index], ...data.card };
        setCards(updatedCards);
        
        toast.success(`Card ${index + 1} saved successfully!`);
        // Notify other tabs/pages to refresh homepage cards
        try {
          localStorage.setItem('crezar_cards_updated', String(Date.now()));
          window.dispatchEvent(new Event('crezar:cardsUpdated'));
        } catch {}
      } else {
        toast.error(data.message || data.error || 'Failed to save card');
      }
    } catch (error) {
      console.error('Error saving card:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to save card. Please try again.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (index) => {
    const card = cards[index];
    if (!card._id) {
      toast.error('Please save the card first');
      return;
    }

    try {
      // Use publish-now endpoint for compatibility
      const response = await axios.post(
        `${API_BASE_URL}/auth/admin/broadcast-cards/${card._id}/publish-now`,
        {},
        { headers: getAuthHeaders() }
      );

      const data = response?.data || {};
      const ok = data.success === true || data.status === 'success';
      if (ok) {
        handleInputChange(index, 'isPublished', !card.isPublished);
        toast.success(`Card published successfully`);
        try {
          localStorage.setItem('crezar_cards_updated', String(Date.now()));
          window.dispatchEvent(new Event('crezar:cardsUpdated'));
        } catch {}
      } else {
        toast.error(data.message || data.error || 'Failed to update card status');
      }
    } catch (error) {
      console.error('Error updating card status:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to update card status';
      toast.error(msg);
    }
  };

  const removeImage = (index) => {
    handleInputChange(index, 'imageUrl', '');
    setPreviewImages(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const deleteCard = async (index) => {
    const card = cards[index];
    // If it's not saved yet, just clear the slot
    if (!card?._id) {
      handleInputChange(index, 'title', `Card ${index + 1}`);
      handleInputChange(index, 'description', '');
      handleInputChange(index, 'imageUrl', '');
      handleInputChange(index, 'isPublished', false);
      return;
    }
    try {
      setSaving(true);
      const response = await axios.delete(
        `${API_BASE_URL}/auth/admin/broadcast-cards/${card._id}`,
        { headers: getAuthHeaders() }
      );
      const data = response?.data || {};
      const ok = data.success === true || data.status === 'success';
      if (ok) {
        toast.success(`Card ${index + 1} deleted`);
        // Reset this slot quickly and re-fetch
        const updated = [...cards];
        updated[index] = {
          title: `Card ${index + 1}`,
          description: '',
          imageUrl: '',
          isPublished: false,
          scheduledDate: new Date().toISOString().split('T')[0],
          scheduleTime: ['15:00','18:00','21:00'][index] || '15:00',
          targetPlan: 'all'
        };
        setCards(updated);
        fetchTodaysCards();
        try {
          localStorage.setItem('crezar_cards_updated', String(Date.now()));
          window.dispatchEvent(new Event('crezar:cardsUpdated'));
        } catch {}
      } else {
        toast.error(data.message || data.error || 'Failed to delete card');
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to delete card';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-yellow-400" />
            <span className="ml-3 text-lg">Loading daily challenge cards...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="w-6 h-6 text-yellow-400" />
          <h1 className="text-2xl font-bold">Daily Challenge Cards Management</h1>
        </div>

        <div className="flex items-center justify-end mb-4">
          <button
            onClick={saveAllCards}
            className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-4 py-2 rounded-md disabled:opacity-60"
            disabled={saving}
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving All...' : 'Save All Cards'}
          </button>
        </div>

        <p className="text-gray-400 text-lg">
          Manage the 3 daily challenge cards that appear on the homepage. These cards are displayed in a 3D stack layout.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div key={index} className="relative group">
              {/* Card Upload Area */}
              <div className="aspect-[4/6] bg-gradient-to-br from-gray-900 via-black to-gray-800 border-2 border-dashed border-yellow-500/30 rounded-2xl overflow-hidden hover:border-yellow-400/50 transition-all duration-300">
                {(card.imageUrl || previewImages[index]) ? (
                  <div className="relative w-full h-full">
                    <img
                      src={
                        previewImages[index] ||
                        (card.imageUrl?.startsWith('http') ? card.imageUrl : `${API_BASE_URL}${card.imageUrl}`)
                      }
                      alt={`Card ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="flex gap-2">
                        <button
                          onClick={() => (cards[index]?._id ? deleteCard(index) : removeImage(index))}
                          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <label className="bg-yellow-500 hover:bg-yellow-600 text-black p-2 rounded-full transition-colors cursor-pointer">
                          <Upload className="w-4 h-4" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleImagePreview(index, file);
                                handleImageUpload(index, file);
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 right-3">
                      <button
                        onClick={() => togglePublish(index)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                          card.isPublished
                            ? 'bg-green-500/90 text-white'
                            : 'bg-gray-500/90 text-gray-200'
                        }`}
                        disabled={!card._id}
                      >
                        {card.isPublished ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {card.isPublished ? 'Live' : 'Draft'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-800/50 transition-colors">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                        <Plus className="w-8 h-8 text-yellow-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                        Upload Card {index + 1}
                      </h3>
                      {index === 1 && (
                        <p className="text-yellow-300 text-sm mb-2">(Featured Card)</p>
                      )}
                      <p className="text-gray-400 text-sm">Click to upload image</p>
                      <p className="text-gray-500 text-xs mt-1">Recommended: 400x600px</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleImagePreview(index, file);
                          handleImageUpload(index, file);
                        }
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Card Info */}
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-yellow-400">
                    Card {index + 1} {index === 1 && '(Featured)'}
                  </h3>
                  {card._id && (
                    <span className="text-xs text-gray-500">ID: {card._id.slice(-6)}</span>
                  )}
                </div>

                {/* Quick Settings */}
                <div className="bg-gray-800/50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-400 min-w-[60px]">Title:</label>
                    <input
                      type="text"
                      value={card.title}
                      onChange={(e) => handleInputChange(index, 'title', e.target.value)}
                      className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:border-yellow-400 focus:outline-none"
                      placeholder={`Card ${index + 1} Title`}
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-white min-w-[60px]">Date:</label>
                    <input
                      type="date"
                      value={toDateInput(card.scheduledDate)}
                      onChange={(e) => handleInputChange(index, 'scheduledDate', e.target.value)}
                      className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:border-yellow-400 focus:outline-none w-full"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-white min-w-[60px]">Time:</label>
                    <input
                      type="time"
                      value={card.scheduleTime || card.scheduledTime || ['15:00','18:00','21:00'][index] || '15:00'}
                      onChange={(e) => handleInputChange(index, 'scheduleTime', e.target.value)}
                      className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:border-yellow-400 focus:outline-none w-full"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-400 min-w-[60px]">Plan:</label>
                    <select
                      value={card.targetPlan}
                      onChange={(e) => handleInputChange(index, 'targetPlan', e.target.value)}
                      className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:border-yellow-400 focus:outline-none"
                    >
                      <option value="all">All Users</option>
                      <option value="free">Free Users</option>
                      <option value="premium">Premium Users</option>
                    </select>
                  </div>
                </div>

                {/* Save Button */}
                <button
                  onClick={() => handleSaveCard(index)}
                  disabled={saving || !card.title.trim()}
                  className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-medium py-2 px-4 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Saving...' : 'Save Card'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl">
          <h3 className="text-lg font-semibold text-blue-400 mb-2">💡 Tips for Daily Challenge Cards</h3>
          <ul className="text-gray-300 space-y-1 text-sm">
            <li>• Card 2 (center) is featured and appears larger in the 3D stack</li>
            <li>• Use high-quality images with 4:6 aspect ratio for best results</li>
            <li>• Keep titles concise and engaging for better user experience</li>
            <li>• Schedule cards for today's date to appear on the homepage</li>
            <li>• Published cards will automatically appear in the homepage 3D stack</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DailyChallengeCards;
